import { resetPosthogUser } from "./posthog-events";

/**
 * The single client-side logout path. Resets the PostHog identity before the
 * full-document navigation to the Auth0 logout route, so a previously identified
 * browser doesn't carry the old person into the next (possibly different) user's
 * session on a shared device.
 *
 * Every CLIENT-initiated logout (profile menu, account-inactive page) routes through here.
 * Server-side `redirect("/auth/logout")` from the auth guards can't run this —
 * no client JS executes on a server redirect — but that's a narrow window
 * (unknown/inactive user), and the next login's identify() overwrites the stale
 * identity anyway.
 */
export const logoutWithReset = (): void => {
  resetPosthogUser();
  // Deliberate full-document navigation: /auth/logout is an Auth0 route
  // handler, not a Next.js page — router.push() would try a client-side
  // transition and never clear the session.
  // eslint-disable-next-line @next/next/no-location-assign-relative-destination
  window.location.href = "/auth/logout";
};

/**
 * Sign in again, as whoever the person chooses, and land on `returnTo`.
 *
 * `prompt=login` (forwarded to Auth0 by the SDK's login route) makes Auth0
 * ask for credentials even though a session exists, which is what lets
 * someone signed in with the wrong account reach a link meant for the other
 * one. No logout first: Auth0 only returns from logout to its allowlisted
 * URLs, and an abandoned sign-in should leave the current session as it was.
 * The callback replaces the session cookie when it completes. The PostHog
 * identity is reset for the same reason as on logout — the next person may
 * be someone else.
 */
export const signInAsSomeoneElse = (returnTo: string): void => {
  resetPosthogUser();
  // Full-document navigation, as above: /auth/login is an Auth0 route handler.
  // eslint-disable-next-line @next/next/no-location-assign-relative-destination
  window.location.href = `/auth/login?prompt=login&returnTo=${encodeURIComponent(returnTo)}`;
};
